import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import './styles.scss';

const getCurrentPosition = (options, value, nShowItems) => {
  const selectedOption = options.indexOf(value);
  if (selectedOption < 0) return 0;
  if (selectedOption > options.length - nShowItems)
    return options.length - nShowItems;
  return selectedOption;
};

const Dropdown = ({
  isOpen = false,
  options = [],
  value = 'November',
  nShowItems = 8,
  onChangeValue,
  className,
  ...restProps
}) => {
  const ref = useRef(null);
  const [toggleOpen, setOpen] = useState(isOpen);
  const [selectedValue, setDropdownValue] = useState(value);
  const [arrowPosition, setCurrentPosition] = useState(
    getCurrentPosition(options, value, nShowItems) || 0,
  );

  useEffect(() => {
    if (value) setDropdownValue(value);
  }, [value]);

  useEffect(() => {
    if (value) {
      const selectedOption = getCurrentPosition(options, value, nShowItems);
      setCurrentPosition(selectedOption);
    }
  }, [value, options, nShowItems]);

  const handleClick = () => {
    setOpen(!toggleOpen);
  };

  const handleText = (ev) => {
    setDropdownValue(ev.currentTarget.textContent);
    onChangeValue(ev.currentTarget.textContent);
    setOpen(false);
  };

  const handleArrowUpClick = () => {
    if (arrowPosition === 0) return;
    setCurrentPosition(arrowPosition - 1);
    setOpen(true);
  };

  const handleArrowDownClick = () => {
    if (!!options && arrowPosition + nShowItems >= options.length) return;
    setCurrentPosition(arrowPosition + 1);
    setOpen(true);
  };

  const handleWheelEvent = (event) => {
    event.stopPropagation();
    if (event.deltaY > 0) {
      handleArrowDownClick();
    } else {
      handleArrowUpClick();
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      event.stopPropagation();
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);

  const renderScrollUpButton = () => {
    return (
      <div className="select_content__up"
        onClick={handleArrowUpClick}
      />
    );
  };

  const renderScrollDownButton = () => {
    return (
      <div className="select_content__down"
        onClick={handleArrowDownClick}
      />
    );
  };

  return (
    <div className={`select_wrapper ${className}`}
      ref={ref}
    >
      <div
        className={toggleOpen ? 'select_content active' : 'select_content'}
        {...restProps}
      >
        <div className="select_content__text"
          onClick={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            handleClick();
          }}
        >
          <span className="select_content__label">{selectedValue || options[0]}</span>
          {toggleOpen ? (
            <MdKeyboardArrowUp size={16} />
          ) : (
            <MdKeyboardArrowDown size={16} />
          )}
        </div>
        <div className="select_content__items"
          onWheel={handleWheelEvent}
        >
          {!!options && arrowPosition > 0 && renderScrollUpButton()}
          {!!options &&
            options.map((item, index) => {
              if (index < arrowPosition) return null;
              if (index >= arrowPosition + nShowItems) return null;
              return (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    handleText(e);
                  }}
                  className={
                    `select_content__item${
                      item.toString() === value
                        ? ' select_content__item-active'
                        : ''}`
                  }
                  key={item.toString()}
                >
                  {item}
                </div>
              );
            })}
          {!!options &&
            options.length - nShowItems - arrowPosition > 0 &&
            renderScrollDownButton()}
        </div>
      </div>
    </div>
  );
};

Dropdown.propTypes = {
  isOpen: PropTypes.bool,
  options: PropTypes.array,
  nShowItems: PropTypes.number,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onChangeValue: PropTypes.func,
};

export default Dropdown;
